import React, { useState } from 'react';
import { Tag, X, Plus, Edit2, Trash2, Check, AlertCircle, Loader2, Layers } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useInventoryCategories,
  useCreateInventoryCategory,
  useUpdateInventoryCategory,
  useDeleteInventoryCategory,
} from '../hooks/useInventory';
import type { InventoryCategory } from '../api/inventory.client';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated?: (categoryName: string) => void;
}

export const CategoryManagerModal: React.FC<Props> = ({ isOpen, onClose, onCategoryCreated }) => {
  const { data: categories = [], isLoading } = useInventoryCategories();
  const createMut = useCreateInventoryCategory();
  const updateMut = useUpdateInventoryCategory();
  const deleteMut = useDeleteInventoryCategory();

  const [newCatName, setNewCatName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<InventoryCategory | null>(null);

  if (!isOpen) return null;

  const handleCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = newCatName.trim();
    if (!trimmed) {
      toast.warning('Ingresa un nombre para la categoría');
      return;
    }

    // Comprobar si ya existe (case insensitive)
    const exists = categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      toast.error(`La categoría "${trimmed}" ya existe`);
      return;
    }

    try {
      await createMut.mutateAsync({ name: trimmed });
      toast.success(`Categoría "${trimmed}" creada con éxito`);
      setNewCatName('');
      if (onCategoryCreated) {
        onCategoryCreated(trimmed);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al crear categoría');
    }
  };

  const handleStartEdit = (cat: InventoryCategory) => {
    setEditingId(cat.id);
    setEditingName(cat.name);
  };

  const handleSaveEdit = async (cat: InventoryCategory) => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      toast.warning('El nombre no puede estar vacío');
      return;
    }

    if (trimmed.toLowerCase() === cat.name.toLowerCase()) {
      setEditingId(null);
      return;
    }

    try {
      await updateMut.mutateAsync({ id: cat.id, data: { name: trimmed } });
      toast.success(`Categoría renombrada a "${trimmed}"`);
      setEditingId(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al actualizar categoría');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteCat) return;
    try {
      await deleteMut.mutateAsync(confirmDeleteCat.id);
      toast.success(`Categoría "${confirmDeleteCat.name}" eliminada`);
      setConfirmDeleteCat(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al eliminar categoría');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div
        className="w-full max-w-lg rounded-2xl bg-surface border border-divider shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-divider px-6 py-4 bg-surface-muted/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Tag size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[color:var(--color-text)]">
                Categorías de Insumos
              </h3>
              <p className="text-xs text-muted font-medium">
                Crea y unifica las categorías para clasificar tu inventario sin duplicados
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Formulario Crear Nueva Categoría */}
        <div className="p-6 border-b border-divider bg-surface">
          <form onSubmit={handleCreate} className="space-y-2">
            <label className="block text-xs font-bold text-muted uppercase tracking-wider">
              Nueva Categoría
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                placeholder="Ej: Lácteos, Carnes, Bebidas..."
                className="flex-1 rounded-xl border border-divider bg-surface px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
              />
              <button
                type="submit"
                disabled={createMut.isPending || !newCatName.trim()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-white shadow-md hover:opacity-90 disabled:opacity-50 transition active:scale-95"
              >
                {createMut.isPending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Plus size={16} />
                )}
                <span>Agregar</span>
              </button>
            </div>
          </form>
        </div>

        {/* Lista de Categorías */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2.5">
          <div className="flex items-center justify-between text-xs text-muted font-semibold mb-2">
            <span>Categorías registradas ({categories.length})</span>
            <span>Insumos asociados</span>
          </div>

          {isLoading ? (
            <div className="py-12 text-center text-muted space-y-2">
              <Loader2 size={24} className="animate-spin text-primary mx-auto" />
              <p className="text-xs">Cargando categorías...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="py-10 text-center text-muted border border-dashed border-divider rounded-2xl p-6">
              <Tag size={36} className="mx-auto mb-2 opacity-30 text-primary" />
              <p className="text-sm font-semibold">No hay categorías creadas aún</p>
              <p className="text-xs mt-1">Escribe un nombre arriba para crear la primera.</p>
            </div>
          ) : (
            categories.map(cat => {
              const isEditing = editingId === cat.id;

              return (
                <div
                  key={cat.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-divider bg-surface hover:bg-surface-muted/30 transition group"
                >
                  {isEditing ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        className="flex-1 rounded-lg border border-primary bg-surface px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveEdit(cat);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <button
                        onClick={() => handleSaveEdit(cat)}
                        disabled={updateMut.isPending}
                        className="rounded-lg bg-emerald-600 p-1.5 text-white hover:bg-emerald-700 transition"
                        title="Guardar cambio"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="rounded-lg bg-surface-muted p-1.5 text-muted hover:text-[color:var(--color-text)] transition"
                        title="Cancelar"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                          <Tag size={13} />
                        </span>
                        <span className="text-sm font-bold text-[color:var(--color-text)] truncate">
                          {cat.name}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Contador de insumos */}
                        <span className="rounded-full bg-surface-muted px-2.5 py-0.5 text-xs font-semibold text-muted border border-divider/60">
                          {cat.items_count ?? 0} {cat.items_count === 1 ? 'insumo' : 'insumos'}
                        </span>

                        {/* Botón Editar */}
                        <button
                          onClick={() => handleStartEdit(cat)}
                          className="rounded-lg p-1.5 text-muted hover:text-primary hover:bg-primary/10 transition"
                          title="Renombrar categoría"
                        >
                          <Edit2 size={13} />
                        </button>

                        {/* Botón Eliminar */}
                        <button
                          onClick={() => setConfirmDeleteCat(cat)}
                          className="rounded-lg p-1.5 text-muted hover:text-red-600 hover:bg-red-50 transition"
                          title="Eliminar categoría"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-divider px-6 py-3.5 bg-surface-muted/30 flex items-center justify-end">
          <button
            onClick={onClose}
            className="rounded-xl bg-surface px-5 py-2 text-xs font-bold text-[color:var(--color-text)] border border-divider hover:bg-surface-muted transition shadow-sm"
          >
            Listo
          </button>
        </div>
      </div>

      {/* Modal Confirmación de Eliminación */}
      {confirmDeleteCat && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl border border-divider animate-in zoom-in-95">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600">
              <AlertCircle size={24} />
            </div>
            <h3 className="text-base font-bold text-[color:var(--color-text)]">
              ¿Eliminar categoría "{confirmDeleteCat.name}"?
            </h3>
            <p className="mt-2 text-xs text-muted leading-relaxed">
              {Number(confirmDeleteCat.items_count) > 0 ? (
                <span>
                  Hay <strong className="text-[color:var(--color-text)]">{confirmDeleteCat.items_count} insumos</strong> clasificados bajo esta categoría. Si la eliminas, esos insumos permanecerán activos pero quedarán sin categoría asignada.
                </span>
              ) : (
                <span>Esta categoría no tiene insumos asociados actualmente. Se eliminará de la lista oficial.</span>
              )}
            </p>
            <div className="mt-5 flex justify-end gap-2.5">
              <button
                onClick={() => setConfirmDeleteCat(null)}
                className="rounded-xl px-4 py-2 text-xs font-bold text-muted hover:bg-surface-muted transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-5 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-60 transition shadow-sm"
              >
                <Trash2 size={14} />
                {deleteMut.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
